import { logger } from "@/utils/logger";
import SecureStorage from './secureStorage';

interface SessionLeadTracker {
  sessionId: string;
  submittedContacts: Array<{
    name?: string;
    phone?: string;
    email?: string;
    timestamp: number;
  }>;
}

const STORAGE_KEY = 'session_lead_tracker';

/**
 * Utility to track and prevent duplicate lead submissions within a session
 */
export const AutoLeadSubmission = {
  /**
   * Check if a lead for this contact has already been submitted in this session
   */
  async hasBeenSubmitted(
    sessionId: string,
    contact: { Name?: string; Phone?: string; Email?: string }
  ): Promise<boolean> {
    try {
      const trackerData = await SecureStorage.getItem(STORAGE_KEY);
      
      // Validate trackerData structure
      if (!trackerData || typeof trackerData !== 'object') {
        logger.log('🎯 hasBeenSubmitted: No tracker data or invalid format - lead NOT submitted');
        return false;
      }
      
      if (trackerData.sessionId !== sessionId) {
        logger.log('🎯 hasBeenSubmitted: Different session ID - lead NOT submitted');
        logger.log('   Tracker sessionId:', trackerData.sessionId, 'vs current:', sessionId);
        return false;
      }
      
      // Validate submittedContacts array
      if (!Array.isArray(trackerData.submittedContacts)) {
        logger.log('🎯 hasBeenSubmitted: No submittedContacts array - lead NOT submitted');
        return false;
      }

      // Check if this contact already exists in submitted list
      const normalized = {
        name: contact.Name?.toLowerCase().trim(),
        phone: contact.Phone?.replace(/\D/g, ''), // Remove non-digits for comparison
        email: contact.Email?.toLowerCase().trim()
      };

      logger.log('🎯 hasBeenSubmitted: Checking for duplicates with normalized:', normalized);
      logger.log('🎯 hasBeenSubmitted: Existing submitted contacts:', trackerData.submittedContacts.length);

      const isDuplicate = trackerData.submittedContacts.some((submitted: any) => {
        if (!submitted) return false;
        
        const submittedNormalized = {
          name: submitted.name?.toLowerCase()?.trim(),
          phone: submitted.phone?.replace(/\D/g, ''),
          email: submitted.email?.toLowerCase()?.trim()
        };

        // Match if phone OR email matches (name can vary slightly)
        const phoneMatch = normalized.phone && submittedNormalized.phone && submittedNormalized.phone === normalized.phone;
        const emailMatch = normalized.email && submittedNormalized.email && submittedNormalized.email === normalized.email;
        
        return phoneMatch || emailMatch;
      });

      if (isDuplicate) {
        logger.log('🚫 Duplicate lead detected for session:', sessionId);
        logger.log('🚫 Duplicate lead detected for session:', sessionId);
      } else {
        logger.log('✅ No duplicate found - lead has NOT been submitted yet');
      }

      return isDuplicate;
    } catch (error) {
      console.error('❌ Error checking lead submission status:', error);
      logger.error('❌ Error checking lead submission status:', error);
      return false; // Assume not submitted on error to be safe
    }
  },

  /**
   * Mark a contact as having been submitted in this session
   */
  async markAsSubmitted(
    sessionId: string,
    contact: { Name?: string; Phone?: string; Email?: string }
  ): Promise<void> {
    try {
      let trackerData: SessionLeadTracker = await SecureStorage.getItem(STORAGE_KEY);

      if (!trackerData || trackerData.sessionId !== sessionId) {
        // New session - create fresh tracker
        trackerData = {
          sessionId,
          submittedContacts: []
        };
      }

      // Add this contact to submitted list
      trackerData.submittedContacts.push({
        name: contact.Name,
        phone: contact.Phone,
        email: contact.Email,
        timestamp: Date.now()
      });

      await SecureStorage.setItem(STORAGE_KEY, trackerData);
      logger.log('✅ Marked contact as submitted for session:', sessionId);
    } catch (error) {
      logger.error('❌ Error marking lead as submitted:', error);
    }
  },

  /**
   * Clear lead tracker for a session (useful on new conversation)
   */
  async clearSession(): Promise<void> {
    try {
      await SecureStorage.removeItem(STORAGE_KEY);
      logger.log('🗑️ Cleared lead submission tracker');
    } catch (error) {
      logger.error('❌ Error clearing lead tracker:', error);
    }
  },

  /**
   * Check if contact data is valid for lead submission
   */
  hasValidContactData(contact?: { Name?: string; First_Name?: string; Last_Name?: string; Phone?: string; Email?: string }): boolean {
    if (!contact) {
      logger.log('🔥 hasValidContactData: No contact object');
      return false;
    }
    
    // Support both formats: 
    // 1. Single "Name" field (old format)
    // 2. Separate "First_Name" and "Last_Name" fields (new format)
    const fullName = contact.Name || 
      (contact.First_Name || contact.Last_Name 
        ? `${contact.First_Name || ''} ${contact.Last_Name || ''}`.trim() 
        : '');
    
    const hasName = !!fullName && fullName.length > 0;
    const hasPhone = !!contact.Phone && contact.Phone.trim().length > 0;
    const hasEmail = !!contact.Email && contact.Email.trim().length > 0;

    
    // Must have name AND (phone OR email)
    return hasName && (hasPhone || hasEmail);
  }
};
